import { useState } from "react";
import { toast } from "sonner";
import { ScrollText, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";

// Blocks the app for authenticated users who must (re-)accept updated required policies.
export default function LegalGate() {
  const { user, loadUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  const pending = user?.legal_pending || [];
  if (!user || pending.length === 0) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/legal/accept", {});
      toast.success("Thank you — your acceptance has been recorded.");
      await loadUser();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="legal-gate" className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="lux-card max-w-lg w-full p-8 min-w-0">
        <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-5">
          <ScrollText className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <h2 className="font-serif-lux text-2xl font-light">We've updated our policies</h2>
        <p className="text-white/60 text-sm mt-3">
          To continue using Admin Restaurant Intelligence, please review and accept the updated documents below.
        </p>
        <ul className="mt-5 space-y-2">
          {pending.map((p) => (
            <li key={p.slug} className="flex items-center justify-between gap-3 rounded-lg bg-[#1A1A1D] px-4 py-3 min-w-0">
              <a href={`/legal/${p.slug}`} target="_blank" rel="noopener noreferrer"
                data-testid={`legal-gate-link-${p.slug}`}
                className="text-sm text-[#D4AF37] hover:underline truncate">{p.title}</a>
              <span className="text-[10px] font-mono-lux text-white/30 shrink-0">v{p.version}</span>
            </li>
          ))}
        </ul>
        <label className="flex items-start gap-3 mt-6 cursor-pointer">
          <input type="checkbox" data-testid="legal-gate-checkbox" checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#D4AF37] shrink-0" />
          <span className="text-sm text-white/70">
            I have read and agree to the updated documents listed above.
          </span>
        </label>
        <button onClick={accept} disabled={!checked || busy} data-testid="legal-gate-accept-button"
          className="gold-btn w-full rounded-lg py-3 text-sm mt-6 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accept and continue"}
        </button>
      </div>
    </div>
  );
}
